import { Router, Routes } from '@angular/router';
import {
  authGuard,
  returnUrlGuard,
  preserveContextGuard,
  preserveContextChildGuard,
  ROUTE,
  ROUTE_DATA,
  TeamsService,
  MATOMO_LABELS,
  RecentProjectsListServiceStrategy,
  RecentProjectsStoreService,
  TeamFacadeStrategy,
  TeamServiceStrategy,
  ProjectFacadeStrategy,
  RecentProjectsListService,
  AuthStoreStrategy,
  AuthStoreService,
  AuthorizationService,
  AuthorizationStrategy,
  ProjectsServiceStrategy,
  ProjectsService,
  ProjectMapperStrategy,
  ModelFacadeStrategy,
  ModelsServiceStrategy,
  ModelMapperStrategy,
  CRATE_NEW_TEAM_DIALOG_TOKEN,
} from 'shared';
import {
  AppTemplateComponent,
  CreateFormComponent,
  CreateFormStrategy,
  CreateTeamStrategy,
} from 'ui';
import { provideTeamDomain, TeamFacade } from '@web-portal/domains/team/anti-corruption-layer';
import {
  ProjectFacade,
  ProjectMapper,
  provideProjectDomain,
} from '@web-portal/domains/project/anti-corruption-layer';
import {
  ModelFacade,
  ModelMapper,
  ModelsService,
  provideModelsDomain,
} from '@web-portal/domains/models/anti-corruption-layer';
import { termsAcceptanceGuard } from './guards/terms-acceptance.guard';
import { inject } from '@angular/core';

export const routes: Routes = [
  {
    path: ROUTE.signUp,
    loadChildren: () => import('signup').then(m => m.routes),
  },
  {
    path: ROUTE.login,
    loadChildren: () => import('login').then(m => m.routes),
    providers: [{ provide: AuthorizationStrategy, useExisting: AuthorizationService }],
  },
  {
    path: ROUTE.terms,
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/terms/terms-page.component').then(m => m.TermsPageComponent),
  },
  {
    path: ROUTE.toc,
    loadComponent: () => import('./pages/toc/toc-page.component').then(m => m.TocPageComponent),
  },
  {
    path: ROUTE.imprint,
    loadComponent: () =>
      import('./pages/imprint/imprint-page.component').then(m => m.ImprintPageComponent),
  },
  {
    path: '',
    canActivate: [preserveContextGuard, authGuard, returnUrlGuard, termsAcceptanceGuard],
    canActivateChild: [preserveContextChildGuard],
    component: AppTemplateComponent,
    providers: [
      { provide: ProjectMapperStrategy, useClass: ProjectMapper },
      { provide: TeamServiceStrategy, useClass: TeamsService },
      { provide: TeamFacadeStrategy, useClass: TeamFacade },
      { provide: ProjectFacadeStrategy, useClass: ProjectFacade },
      { provide: AuthStoreStrategy, useExisting: AuthStoreService },
      { provide: ProjectsServiceStrategy, useClass: ProjectsService },
      { provide: ModelMapperStrategy, useClass: ModelMapper },
      { provide: ModelFacadeStrategy, useClass: ModelFacade },
      { provide: ModelsServiceStrategy, useClass: ModelsService },
      provideTeamDomain(),
      provideProjectDomain(),
      provideModelsDomain(),
    ],
    children: [
      {
        path: '',
        pathMatch: 'full',
        providers: [
          RecentProjectsStoreService,
          {
            provide: CRATE_NEW_TEAM_DIALOG_TOKEN,
            useValue: {
              component: CreateFormComponent,
              providers: [{ provide: CreateFormStrategy, useValue: new CreateTeamStrategy() }],
              onSubmit: async (result: unknown) => {
                if (result) {
                  await inject(Router).navigate(['/' + ROUTE.teams, (result as { id: string }).id]);
                }
              },
            },
          },
          {
            provide: RecentProjectsListServiceStrategy,
            useClass: RecentProjectsListService,
          },
        ],
        loadChildren: () => import('recent-projects').then(m => m.routes),
      },
      {
        path: ROUTE.teams + '/:teamId',
        data: {
          [ROUTE_DATA.matomoSegments]: MATOMO_LABELS[ROUTE.teams],
        },
        loadChildren: () => import('team-dashboard').then(m => m.routes),
      },
    ],
  },
  /**
   * Fallback for a weird redirect issue
   * To be investigated later
   */
  {
    path: 'http:',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'https:',
    redirectTo: '',
    pathMatch: 'full',
  },
];
